import React, { useState, useEffect } from 'react';
import { FiX, FiPlus, FiTrash2 } from 'react-icons/fi';

export default function TourGuideForm({ isOpen, onClose, onSubmit, initialData }) {
  const [formData, setFormData] = useState({
    name: '',
    name_arabic: '',
    phone: '',
    email: '',
    languages: [{ code: 'en', name: 'English', proficiency: 'fluent' }],
    specialties: [],
    service_areas: [],
    tour_types: [],
    experience_years: 0,
    pricing: {
      daily_rate: 0,
      hourly_rate: 0,
      currency: 'USD',
    },
    bio: {
      short: '',
      long: '',
    },
    status: 'pending_verification',
  });
  const [loading, setLoading] = useState(false);
  const [newLanguage, setNewLanguage] = useState({ code: 'en', name: 'English', proficiency: 'fluent' });

  const languageOptions = [
    { code: 'en', name: 'English' },
    { code: 'ar', name: 'Arabic' },
    { code: 'de', name: 'German' },
    { code: 'fr', name: 'French' },
    { code: 'it', name: 'Italian' },
    { code: 'pl', name: 'Polish' },
    { code: 'cs', name: 'Czech' },
    { code: 'ru', name: 'Russian' },
    { code: 'es', name: 'Spanish' },
  ];

  const specialtyOptions = [
    'historical', 'cultural', 'adventure', 'marine', 'luxury', 'city_tours'
  ];

  const serviceAreas = [
    'hurghada', 'marsa_alam', 'luxor', 'aswan', 'cairo', 'alexandria', 'sharm_el_sheikh'
  ];

  const tourTypeOptions = [
    'private', 'group', 'small_group', 'luxury', 'educational'
  ];

  useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name || '',
        name_arabic: initialData.name_arabic || '',
        phone: initialData.phone || '',
        email: initialData.email || '',
        languages: initialData.languages || [{ code: 'en', name: 'English', proficiency: 'fluent' }],
        specialties: initialData.specialties || [],
        service_areas: initialData.service_areas || [],
        tour_types: initialData.tour_types || [],
        experience_years: initialData.experience_years || 0,
        pricing: initialData.pricing || { daily_rate: 0, hourly_rate: 0, currency: 'USD' },
        bio: initialData.bio || { short: '', long: '' },
        status: initialData.status || 'pending_verification',
      });
    }
  }, [initialData]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData({
        ...formData,
        [parent]: {
          ...formData[parent],
          [child]: value,
        },
      });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleAddLanguage = () => {
    if (newLanguage.code && !formData.languages.some(l => l.code === newLanguage.code)) {
      setFormData({
        ...formData,
        languages: [...formData.languages, newLanguage],
      });
    }
    setNewLanguage({ code: 'en', name: 'English', proficiency: 'fluent' });
  };

  const handleRemoveLanguage = (code) => {
    setFormData({
      ...formData,
      languages: formData.languages.filter(l => l.code !== code),
    });
  };

  const handleSpecialtyToggle = (specialty) => {
    setFormData({
      ...formData,
      specialties: formData.specialties.includes(specialty)
        ? formData.specialties.filter(s => s !== specialty)
        : [...formData.specialties, specialty],
    });
  };

  const handleAreaToggle = (area) => {
    setFormData({
      ...formData,
      service_areas: formData.service_areas.includes(area)
        ? formData.service_areas.filter(a => a !== area)
        : [...formData.service_areas, area],
    });
  };

  const handleTourTypeToggle = (type) => {
    setFormData({
      ...formData,
      tour_types: formData.tour_types.includes(type)
        ? formData.tour_types.filter(t => t !== type)
        : [...formData.tour_types, type],
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    await onSubmit(formData);
    setLoading(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b sticky top-0 bg-white">
          <h2 className="text-xl font-semibold">{initialData ? 'Edit Tour Guide' : 'Add New Tour Guide'}</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg">
            <FiX className="w-5 h-5" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name (English) *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name (Arabic)</label>
              <input
                type="text"
                name="name_arabic"
                value={formData.name_arabic}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Experience (Years)</label>
              <input
                type="number"
                name="experience_years"
                value={formData.experience_years}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-primary-500"
              >
                <option value="available">Available</option>
                <option value="busy">Busy</option>
                <option value="inactive">Inactive</option>
                <option value="on_leave">On Leave</option>
                <option value="pending_verification">Pending Verification</option>
              </select>
            </div>
          </div>

          {/* Languages */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Languages</label>
            <div className="flex flex-wrap gap-2 mb-3">
              {formData.languages.map((lang) => (
                <span key={lang.code} className="flex items-center bg-gray-100 px-3 py-1 rounded-full text-sm">
                  {lang.name} ({lang.proficiency})
                  <button
                    type="button"
                    onClick={() => handleRemoveLanguage(lang.code)}
                    className="ml-2 text-gray-500 hover:text-red-500"
                  >
                    <FiTrash2 className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <select
                value={newLanguage.code}
                onChange={(e) => {
                  const selected = languageOptions.find(l => l.code === e.target.value);
                  setNewLanguage({ ...newLanguage, code: e.target.value, name: selected?.name || '' });
                }}
                className="px-3 py-2 border border-gray-300 rounded-lg"
              >
                {languageOptions.map(lang => (
                  <option key={lang.code} value={lang.code}>{lang.name}</option>
                ))}
              </select>
              <select
                value={newLanguage.proficiency}
                onChange={(e) => setNewLanguage({ ...newLanguage, proficiency: e.target.value })}
                className="px-3 py-2 border border-gray-300 rounded-lg"
              >
                <option value="basic">Basic</option>
                <option value="conversational">Conversational</option>
                <option value="fluent">Fluent</option>
                <option value="native">Native</option>
              </select>
              <button
                type="button"
                onClick={handleAddLanguage}
                className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600"
              >
                <FiPlus />
              </button>
            </div>
          </div>

          {/* Specialties */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Specialties</label>
            <div className="grid grid-cols-3 gap-2">
              {specialtyOptions.map(spec => (
                <label key={spec} className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.specialties.includes(spec)}
                    onChange={() => handleSpecialtyToggle(spec)}
                    className="mr-2"
                  />
                  <span className="text-sm capitalize">{spec.replace('_', ' ')}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Service Areas */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Service Areas</label>
            <div className="grid grid-cols-3 gap-2">
              {serviceAreas.map(area => (
                <label key={area} className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.service_areas.includes(area)}
                    onChange={() => handleAreaToggle(area)}
                    className="mr-2"
                  />
                  <span className="text-sm capitalize">{area.replace('_', ' ')}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Tour Types */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Tour Types</label>
            <div className="grid grid-cols-3 gap-2">
              {tourTypeOptions.map(type => (
                <label key={type} className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.tour_types.includes(type)}
                    onChange={() => handleTourTypeToggle(type)}
                    className="mr-2"
                  />
                  <span className="text-sm capitalize">{type.replace('_', ' ')}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Pricing */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm text-gray-600 mb-1">Daily Rate ($)</label>
              <input
                type="number"
                name="pricing.daily_rate"
                value={formData.pricing.daily_rate}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1">Hourly Rate ($)</label>
              <input
                type="number"
                name="pricing.hourly_rate"
                value={formData.pricing.hourly_rate}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1">Currency</label>
              <select
                name="pricing.currency"
                value={formData.pricing.currency}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              >
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
                <option value="GBP">GBP</option>
              </select>
            </div>
          </div>

          {/* Bio */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Short Bio</label>
            <input
              type="text"
              name="bio.short"
              value={formData.bio.short}
              onChange={handleChange}
              placeholder="e.g., Professional Egyptologist with 8 years of experience"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Full Bio</label>
            <textarea
              name="bio.long"
              value={formData.bio.long}
              onChange={handleChange}
              rows={3}
              placeholder="Detailed description of the tour guide's expertise and experience..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-50 transition"
            >
              {loading ? 'Saving...' : initialData ? 'Update Tour Guide' : 'Create Tour Guide'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
